"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { WindowPlot } from "@/components/tools/biology-genetics/window-plot";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  type Composition,
  countComposition,
  cpgObservedExpected,
  cumulativeSkew,
  type GcMethod,
  gcByCodonPosition,
  gcFraction,
  gcSkew,
  windowProfile,
} from "@/lib/tool-kit/biology-genetics/composition";
import { parseFasta } from "@/lib/tool-kit/biology-genetics/fasta";
import { cleanNucleotides } from "@/lib/tool-kit/biology-genetics/iupac";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";

/** English strings. A translated content file overrides these through `ui`. */
export const GC_CONTENT_CALCULATOR_STRINGS = {
  inputLabel: "DNA or RNA sequence — plain text or FASTA",
  inputHint:
    "Spaces, line breaks, numbers and lower case are fine. With several FASTA records, each gets a row in the table and you choose one to plot.",
  inputPlaceholder: ">my_gene\nATGGCCCTGTGG…",
  exampleHeader: "NM_000207.3 Homo sapiens insulin (INS), coding sequence",
  tryExample: "Try an example",
  methodLabel: "Ambiguity codes (N, R, Y…)",
  methodUnambiguous:
    "Leave them out — count G, C and S over A, C, G, T/U, S and W",
  methodLength: "Count them as not GC — divide by the full length",
  methodWeighted: "Weight them by their chance of being G or C",
  windowLabel: "Window size (bases)",
  stepLabel: "Step (bases)",
  plotLabel: "Plot",
  plotGc: "GC content per window",
  plotSkew: "GC skew per window",
  plotCumulative: "Cumulative GC skew",
  recordLabel: "Sequence to plot",
  results: "Results",
  unnamed: "Sequence {n}",
  gcContent: "GC content",
  length: "Length",
  skew: "GC skew (G − C) / (G + C)",
  unitNt: "nt",
  codonGc:
    "GC at codon positions 1, 2, 3 (frame from base 1): {gc1}, {gc2}, {gc3}",
  cpg: "CpG observed / expected: {value}",
  cpgNone: "CpG observed / expected: not defined (no C or G)",
  tableHeading: "All sequences",
  nameColumn: "Sequence",
  lengthColumn: "Length",
  gcColumn: "GC %",
  skewColumn: "GC skew",
  otherColumn: "Other",
  copyTable: "Copy table (TSV)",
  copied: "Copied!",
  plotHeading: "Sliding-window profile",
  plotAria: "Line plot of {metric} along {name}",
  plotDescription:
    "{metric} in windows of {size} bases, stepping {step} bases, along a sequence of {length} bases.",
  xAxis: "Position (bases)",
  yGc: "GC content (%)",
  ySkew: "GC skew",
  yCumulative: "Cumulative GC skew",
  meanLine: "Dashed line: whole-sequence value.",
  windowFixed:
    "The window was longer than the sequence, so it was shortened to {size} bases.",
  thinned: "To keep the plot readable the step was raised to {step} bases.",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  downloadName: "gc-profile",
  ignored: "Ignored characters that are not nucleotide codes: {list}.",
  prompt: "Paste a DNA or RNA sequence to measure its GC content.",
  errorNone: "No nucleotide letters were found in that text.",
  errorWindow: "Window size and step must be whole numbers of 1 or more.",
} as const;

type Strings = typeof GC_CONTENT_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
type PlotKind = "gc" | "skew" | "cumulative";

const EXAMPLE_SEQUENCE =
  "ATGGCCCTGTGGATGCGCCTCCTGCCCCTGCTGGCGCTGCTGGCCCTCTGGGGACCTGACCCAGCCGCAG" +
  "CCTTTGTGAACCAACACCTGTGCGGCTCACACCTGGTGGAAGCTCTCTACCTAGTGTGCGGGGAACGAGG" +
  "CTTCTTCTACACACCCAAGACCCGCCGGGAGGCAGAGGACCTGCAGGTGGGGCAGGTGGAGCTGGGCGGG" +
  "GGCCCTGGTGCAGGCAGCCTGCAGCCCTTGGCCCTGGAGGGGTCCCTGCAGAAGCGTGGCATTGTGGAAC" +
  "AATGCTGTACCAGCATCTGCTCCCTCTACCAGCTGGAGAACTACTGCAACTAG";

interface Row {
  name: string;
  sequence: string;
  counts: Composition;
  gc: number | null;
  skew: number;
}

function percent(fmt: Fmt, fraction: number | null): string {
  return fraction === null
    ? "—"
    : `${fmt(fraction * 100, { maximumFractionDigits: 2 })}%`;
}

function tsv(rows: readonly Row[]): string {
  const head = [
    "name",
    "length",
    "A",
    "C",
    "G",
    "T/U",
    "other",
    "GC_percent",
    "GC_skew",
  ].join("\t");
  const body = rows.map((row) =>
    [
      row.name,
      row.counts.length,
      row.counts.a,
      row.counts.c,
      row.counts.g,
      row.counts.t,
      row.counts.ambiguous + row.counts.other,
      row.gc === null ? "" : Number((row.gc * 100).toFixed(4)),
      Number(row.skew.toFixed(4)),
    ].join("\t")
  );
  return [head, ...body].join("\n");
}

function SummaryTable({
  rows,
  strings,
  fmt,
}: {
  rows: readonly Row[];
  strings: Strings;
  fmt: Fmt;
}) {
  const heads = [
    strings.nameColumn,
    strings.lengthColumn,
    "A",
    "C",
    "G",
    "T/U",
    strings.otherColumn,
    strings.gcColumn,
    strings.skewColumn,
  ];
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            {heads.map((head) => (
              <th className={TH} key={head} scope="col">
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: records may share a name
            <tr key={index}>
              <td className={TD}>{row.name}</td>
              {[
                row.counts.length,
                row.counts.a,
                row.counts.c,
                row.counts.g,
                row.counts.t,
                row.counts.ambiguous + row.counts.other,
              ].map((value, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed column order
                <td className={`${TD} text-right`} key={i}>
                  {fmt(value)}
                </td>
              ))}
              <td className={`${TD} text-right`}>{percent(fmt, row.gc)}</td>
              <td className={`${TD} text-right`}>
                {fmt(row.skew, { maximumFractionDigits: 3 })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlotSection({
  row,
  kind,
  method,
  windowSize,
  step,
  strings,
  fmt,
}: {
  row: Row;
  kind: PlotKind;
  method: GcMethod;
  windowSize: number;
  step: number;
  strings: Strings;
  fmt: Fmt;
}) {
  const profile = windowProfile(row.sequence, windowSize, step, method);
  if (profile === null) {
    return null;
  }
  const metric = {
    gc: strings.plotGc,
    skew: strings.plotSkew,
    cumulative: strings.plotCumulative,
  }[kind];
  const cumulative =
    kind === "cumulative" ? cumulativeSkew(row.sequence, profile.size) : null;
  let xs: readonly number[] = profile.centres;
  let ys: readonly (number | null)[] = profile.skew;
  if (kind === "gc") {
    ys = profile.gc.map((v) => (v === null ? null : v * 100));
  } else if (cumulative) {
    xs = cumulative.positions;
    ys = cumulative.values;
  }
  const reference = {
    gc: row.gc === null ? null : row.gc * 100,
    skew: row.skew,
    cumulative: null,
  }[kind];
  const yTitle = {
    gc: strings.yGc,
    skew: strings.ySkew,
    cumulative: strings.yCumulative,
  }[kind];
  const decimals = kind === "gc" ? 1 : 2;
  const params = {
    metric,
    name: row.name,
    size: fmt(profile.size),
    step: fmt(profile.step),
    length: fmt(row.sequence.length),
  };

  function save(kindOfFile: "svg" | "png") {
    const svg = document.getElementById("gcc-plot");
    if (!(svg instanceof SVGSVGElement)) {
      return;
    }
    const name = `${strings.downloadName}-${kind}`;
    if (kindOfFile === "svg") {
      downloadSvg(`${name}.svg`, svg);
    } else {
      downloadPng(`${name}.png`, svg, { scale: 2 });
    }
  }

  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.plotHeading}</h3>
      {profile.thinned ? (
        <Notice>{t(strings.thinned, { step: fmt(profile.step) })}</Notice>
      ) : null}
      <WindowPlot
        description={t(strings.plotDescription, params)}
        format={(value) => fmt(value, { maximumFractionDigits: decimals })}
        id="gcc-plot"
        label={t(strings.plotAria, params)}
        reference={reference}
        series={{ xs, ys }}
        xTitle={strings.xAxis}
        yTitle={yTitle}
      />
      {reference === null ? null : <p className={HINT}>{strings.meanLine}</p>}
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => save("svg")}
          type="button"
        >
          {strings.downloadSvg}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => save("png")}
          type="button"
        >
          {strings.downloadPng}
        </button>
      </div>
    </div>
  );
}

export default function GcContentCalculatorWidget() {
  const strings = useToolUi(GC_CONTENT_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState(
    `>${strings.exampleHeader}\n${EXAMPLE_SEQUENCE}`
  );
  const [method, setMethod] = useState<GcMethod>("unambiguous");
  const [windowRaw, setWindowRaw] = useState("100");
  const [stepRaw, setStepRaw] = useState("20");
  const [kind, setKind] = useState<PlotKind>("gc");
  const [picked, setPicked] = useState("0");

  const records = parseFasta(text);
  const cleaned = records.map((record) => cleanNucleotides(record.sequence));
  const invalid = [...new Set(cleaned.flatMap((entry) => entry.invalid))];
  const rows: Row[] = records
    .map((record, index) => {
      const sequence = cleaned[index].sequence;
      const counts = countComposition(sequence);
      return {
        name: record.name || t(strings.unnamed, { n: fmt(index + 1) }),
        sequence,
        counts,
        gc: gcFraction(counts, method),
        skew: gcSkew(counts.g, counts.c),
      };
    })
    .filter((row) => row.sequence !== "");
  const row = rows[Math.min(Number(picked), rows.length - 1)] ?? null;
  const windowSize = parseIntegerInput(windowRaw);
  const step = parseIntegerInput(stepRaw);
  const windowsValid =
    windowSize !== null && windowSize >= 1 && step !== null && step >= 1;

  let message: string | null = null;
  if (text.trim() === "") {
    message = strings.prompt;
  } else if (rows.length === 0) {
    message = strings.errorNone;
  }
  const codon = row ? gcByCodonPosition(row.sequence) : null;
  const cpg = row ? cpgObservedExpected(row.counts) : null;

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="gcc-input"
        label={strings.inputLabel}
        onChange={(value) => {
          setText(value);
          setPicked("0");
        }}
        placeholder={strings.inputPlaceholder}
        rows={7}
        value={text}
      />
      <button
        className={BUTTON_SECONDARY}
        onClick={() =>
          setText(`>${strings.exampleHeader}\n${EXAMPLE_SEQUENCE}`)
        }
        type="button"
      >
        {strings.tryExample}
      </button>
      <div className={`${CARD} space-y-4`}>
        <SelectField
          id="gcc-method"
          label={strings.methodLabel}
          onChange={setMethod}
          options={[
            { value: "unambiguous", label: strings.methodUnambiguous },
            { value: "length", label: strings.methodLength },
            { value: "weighted", label: strings.methodWeighted },
          ]}
          value={method}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="gcc-window"
            label={strings.windowLabel}
            mono
            numeric
            onChange={setWindowRaw}
            value={windowRaw}
          />
          <TextField
            id="gcc-step"
            label={strings.stepLabel}
            mono
            numeric
            onChange={setStepRaw}
            value={stepRaw}
          />
        </div>
      </div>
      {invalid.length > 0 ? (
        <Notice>{t(strings.ignored, { list: invalid.join(" ") })}</Notice>
      ) : null}
      {message ? <Notice>{message}</Notice> : null}
      {row ? (
        <div aria-live="polite" className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          {rows.length > 1 ? (
            <SelectField
              id="gcc-record"
              label={strings.recordLabel}
              onChange={setPicked}
              options={rows.map((entry, index) => ({
                value: String(index),
                label: entry.name,
              }))}
              value={picked}
            />
          ) : null}
          <StatGrid>
            <StatCard
              emphasis
              label={strings.gcContent}
              value={percent(fmt, row.gc)}
            />
            <StatCard
              label={strings.length}
              value={`${fmt(row.counts.length)} ${strings.unitNt}`}
            />
            <StatCard
              label={strings.skew}
              value={fmt(row.skew, { maximumFractionDigits: 3 })}
            />
          </StatGrid>
          {codon ? (
            <p className="text-foreground text-sm">
              {t(strings.codonGc, {
                gc1: percent(fmt, codon[0]),
                gc2: percent(fmt, codon[1]),
                gc3: percent(fmt, codon[2]),
              })}
            </p>
          ) : null}
          <p className="text-foreground text-sm">
            {cpg === null
              ? strings.cpgNone
              : t(strings.cpg, {
                  value: fmt(cpg, { maximumFractionDigits: 2 }),
                })}
          </p>
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
            <SummaryTable fmt={fmt} rows={rows} strings={strings} />
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyTable}
              text={tsv(rows)}
            />
          </div>
          <PillGroup
            label={strings.plotLabel}
            name="gcc-plot-kind"
            onChange={setKind}
            options={[
              { value: "gc", label: strings.plotGc },
              { value: "skew", label: strings.plotSkew },
              { value: "cumulative", label: strings.plotCumulative },
            ]}
            value={kind}
          />
          {windowsValid ? (
            <PlotSection
              fmt={fmt}
              kind={kind}
              method={method}
              row={row}
              step={step}
              strings={strings}
              windowSize={windowSize}
            />
          ) : (
            <Notice>{strings.errorWindow}</Notice>
          )}
        </div>
      ) : null}
    </div>
  );
}
